/**
 * Pflanzenbibliothek auf der Lieferantenseite (`?ansicht=pflanzen`, 16.10.2026).
 *
 * Benjamin: „gerade im GaLaBau-Bereich ist das sehr wichtig, dass wir eine genaue
 * Pflanzenbibliothek haben.“ Links die Pflanzen nach Typ, ungeprüfte zuerst; rechts das Blatt
 * mit Stammdaten, Quellen, Qualitäten und Lieferantenpreisen. Werte, die Claude nicht aus einer
 * Quelle hat, tragen ein „≈“. „Geprüft“ setzt ein Mensch — stichprobenartig reicht.
 * Aufschläge je Gruppe (`?pflanze=aufschlag`): Einkauf × Faktor = Angebotspreis, im Angebot je
 * Zeile änderbar.
 */
import { useMemo, useState } from "react";

import { fehlerText } from "@vimaana/ui";

import {
  useAufschlagSetzen, usePflanzeAendern, usePflanzeBlatt, usePflanzenListe, usePflanzenTypen, usePflanzeRecherche, usePflanzeStatus,
  type PflanzeAenderung, type PflanzeBlatt, type PflanzeZeile,
} from "../../hooks/usePflanzen";
import { euro } from "../../lib/invoices";

const MONAT = ["", "Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
const TYPNAME: Record<string, string> = {
  staude: "Stauden", gras: "Gräser", farn: "Farne", kraut: "Kräuter", zwiebel: "Zwiebeln & Knollen",
  wasser: "Wasser & Sumpf", saisonal: "Einjährige & Balkon", kuebel: "Kübelpflanzen", strauch: "Sträucher",
  rose: "Rosen", kletter: "Kletterpflanzen", obst: "Obst & Beeren", baum: "Bäume",
};
const REIHE = Object.keys(TYPNAME);

function spanne(a: number | null | undefined, b: number | null | undefined, einheit = "cm"): string {
  if (!a && !b) return "";
  return (a && b && a !== b ? `${a}–${b}` : String(a || b)) + (einheit ? ` ${einheit}` : "");
}

function bluete(p: { bluete_von: number | null; bluete_bis: number | null; bluete_farbe: string }): string {
  if (!p.bluete_von) return "";
  const m = MONAT[p.bluete_von] + (p.bluete_bis && p.bluete_bis !== p.bluete_von ? `–${MONAT[p.bluete_bis]}` : "");
  return m + (p.bluete_farbe ? `, ${p.bluete_farbe}` : "");
}

// ── Liste ────────────────────────────────────────────────────────────────

export function PflanzenListe({ gewaehlt, onWahl }: { gewaehlt: string; onWahl: (id: string) => void }) {
  const [q, setQ] = useState("");
  const [nurOffen, setNurOffen] = useState(false);
  const liste = usePflanzenListe(q.trim(), nurOffen ? "vorschlag" : "", "");
  const daten = liste.data ?? [];
  const bloecke = useMemo(() => {
    const aus = new Map<string, PflanzeZeile[]>();
    for (const p of daten) aus.set(p.typ, [...(aus.get(p.typ) ?? []), p]);
    return [...aus.entries()].sort((a, b) => REIHE.indexOf(a[0]) - REIHE.indexOf(b[0]));
  }, [daten]);
  const offen = daten.filter((p) => p.status === "vorschlag").length;

  return (
    <>
      <div className="lf-listensuche">
        <input className="lf-feld" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Botanisch oder deutsch, z. B. Sonnenhut …"
          aria-label="Pflanze suchen" autoComplete="off" />
        <button type="button" className={"lf-knopf" + (nurOffen ? " ist-an" : "")} aria-pressed={nurOffen} onClick={() => setNurOffen((v) => !v)}
          title="Nur Pflanzen, die noch niemand geprüft hat">Zu prüfen{!nurOffen && offen ? ` (${offen})` : ""}</button>
        <button type="button" className={"lf-knopf" + (gewaehlt === "aufschlag" ? " ist-an" : "")} onClick={() => onWahl("aufschlag")}>Aufschläge</button>
      </div>
      <div className="kunden-tabelle">
        {liste.isPending && <p className="lf-leer lf-rand">Lade …</p>}
        {!!liste.error && <p className="lf-fehler lf-rand">{fehlerText(liste.error)}</p>}
        {bloecke.map(([typ, zeilen]) => (
          <section key={typ} className="lf-block">
            <h3 className="lf-blockkopf">{TYPNAME[typ] || typ} <span>{zeilen.length}</span></h3>
            <ul className="lf-tabelle">
              {zeilen.map((p) => (
                <li key={p.id} className={"lf-tzeile" + (gewaehlt === p.id ? " ist-gewaehlt" : "")}>
                  <button type="button" className="lf-tzeile-haupt" onClick={() => onWahl(p.id)}>
                    <span className="lf-tzeile-name pf-botanisch">
                      {p.botanisch}{p.deutsch && <small> · {p.deutsch}</small>}
                    </span>
                    <span className="lf-tzeile-info">
                      {[spanne(p.hoehe_von, p.hoehe_bis), bluete(p),
                        p.ek_ab != null ? `ab ${euro(p.ek_ab)} EK` : p.qualitaeten ? "" : "kein Lieferant"].filter(Boolean).join(" · ") || "—"}
                      {p.status === "vorschlag" && <b className="pf-marke"> ungeprüft</b>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {!liste.isPending && bloecke.length === 0 && <p className="lf-leer lf-rand">Keine Pflanze passt.</p>}
      </div>
    </>
  );
}

// ── Aufschläge je Gruppe ─────────────────────────────────────────────────

export function AufschlagBlatt({ onClose }: { onClose: () => void }) {
  const typen = usePflanzenTypen();
  const setzen = useAufschlagSetzen();
  const [werte, setWerte] = useState<Record<string, string>>({});
  return (
    <div className="lf-detail">
      <header className="lf-detail-kopf">
        <div>
          <p className="lf-detail-ueber">Pflanzenbibliothek</p>
          <h2>Aufschläge je Gruppe</h2>
        </div>
        <button type="button" className="lf-knopf lf-detail-zu" onClick={onClose} aria-label="Schließen">×</button>
      </header>
      <p className="lf-hinweis">
        Angebotspreis = Einkaufspreis des Lieferanten × Faktor. Gilt für neue Angebotszeilen; im Angebot lässt
        sich jede Pflanze einzeln anders rechnen. Pflanzarbeit bleibt eine eigene Position.
      </p>
      {typen.isPending && <p className="lf-leer">Lade …</p>}
      <ul className="lf-liste">
        {(typen.data ?? []).map((t) => {
          const wert = werte[t.typ] ?? String(t.aufschlag).replace(".", ",");
          const zahl = Number(wert.replace(",", "."));
          const geaendert = zahl !== t.aufschlag;
          return (
            <li key={t.typ} className="lf-zeile">
              <span className="lf-zeile-haupt"><span className="lf-zeile-name">{t.name}</span></span>
              <span className="lf-zeile-tat pf-faktor">
                <span>×</span>
                <input className="lf-feld" inputMode="decimal" value={wert} aria-label={`Aufschlag ${t.name}`}
                  onChange={(e) => setWerte({ ...werte, [t.typ]: e.target.value })} />
                <button type="button" className="lf-knopf is-primaer" disabled={!geaendert || !(zahl >= 1 && zahl <= 10) || setzen.isPending}
                  onClick={() => setzen.mutate({ typ: t.typ, faktor: zahl }, { onSuccess: () => setWerte((w) => { const n = { ...w }; delete n[t.typ]; return n; }) })}>
                  Speichern
                </button>
              </span>
            </li>
          );
        })}
      </ul>
      {setzen.isError && <p className="lf-fehler">{fehlerText(setzen.error)}</p>}
    </div>
  );
}

// ── Blatt einer Pflanze ──────────────────────────────────────────────────

export function PflanzeDetail({ pflanzeId, onClose, onLieferant }: {
  pflanzeId: string; onClose: () => void; onLieferant: (id: string) => void;
}) {
  const blatt = usePflanzeBlatt(pflanzeId);
  const status = usePflanzeStatus(pflanzeId);
  const recherche = usePflanzeRecherche(pflanzeId);
  const [bearbeiten, setBearbeiten] = useState(false);
  const d = blatt.data;
  if (blatt.isPending) return <p className="lf-leer">Lade …</p>;
  if (blatt.isError || !d) return <p className="lf-fehler">{fehlerText(blatt.error)}</p>;
  const geschaetzt = (feld: string) => d.aus_wissen.includes(feld)
    ? <span className="pf-wissen" title="Nicht aus einer Quelle, sondern Fachwissen — bitte prüfen"> ≈</span> : null;
  const ja = (v: boolean | null) => (v == null ? "" : v ? "ja" : "nein");
  const zeilen: [string, string, string][] = [
    ["Höhe", spanne(d.hoehe_von, d.hoehe_bis), "hoehe_cm"],
    ["Breite", spanne(d.breite_von, d.breite_bis), "breite_cm"],
    ["Standort", d.standort.join(", "), "standort"],
    ["Boden", d.boden, "boden"],
    ["Feuchte", d.feuchte.join(", "), "feuchte"],
    ["Blüte", bluete(d), "bluete_monate"],
    ["Pflanzdichte", d.stueck_je_m2 ? `${String(d.stueck_je_m2).replace(".", ",")} Stück/m²` : "", "stueck_je_m2"],
    ["Winterhärte", d.winterhaerte, "winterhaerte"],
    ["Heimisch", ja(d.heimisch), "heimisch"],
    ["Insektenfreundlich", ja(d.insektenfreundlich), "insektenfreundlich"],
    ["Immergrün", ja(d.immergruen), "immergruen"],
    ["Giftig", ja(d.giftig), "giftig"],
  ];

  return (
    <div className="lf-detail">
      <header className="lf-detail-kopf">
        <div>
          <p className="lf-detail-ueber">{TYPNAME[d.typ] || d.typ}{d.status === "geprueft" ? " · geprüft" : d.status === "verworfen" ? " · verworfen" : " · ungeprüft"}</p>
          <h2 className="pf-botanisch">{d.botanisch}</h2>
          {d.deutsch && <p className="lf-stamm">{d.deutsch}</p>}
        </div>
        <button type="button" className="lf-knopf lf-detail-zu" onClick={onClose} aria-label="Schließen">×</button>
      </header>

      <div className="lf-knoepfe pf-leiste">
        {d.status !== "geprueft"
          ? <button type="button" className="lf-knopf is-primaer" disabled={status.isPending} onClick={() => status.mutate("geprueft")}>✓ Geprüft</button>
          : <button type="button" className="lf-knopf" disabled={status.isPending} onClick={() => status.mutate("vorschlag")}>Wieder auf „ungeprüft“</button>}
        <button type="button" className="lf-knopf" aria-expanded={bearbeiten} onClick={() => setBearbeiten((v) => !v)}>
          {bearbeiten ? "Fertig" : "Bearbeiten"}
        </button>
        <span className="lf-luecke" />
        {d.status !== "verworfen" && (
          <button type="button" className="lf-knopf is-gefahr" disabled={status.isPending} onClick={() => status.mutate("verworfen")}>Verwerfen</button>
        )}
      </div>
      {d.status === "geprueft" && d.geprueft_von && <p className="lf-hinweis">Geprüft von {d.geprueft_von}.</p>}
      {status.isError && <p className="lf-fehler">{fehlerText(status.error)}</p>}
      {/* Claude auf dem Evo (16.10.2026): neue Pflanzen aus Belegen von selbst, sonst auf Knopfdruck.
          Übernommen wird nur in leere oder mit ≈ markierte Felder. */}
      {d.recherche?.zustand === "laeuft" ? (
        <p className="lf-hinweis pf-recherche">Claude recherchiert diese Pflanze auf dem Evo — meist ein bis drei Minuten, die Seite aktualisiert sich selbst.</p>
      ) : d.status !== "verworfen" && (!d.quellen.length || d.aus_wissen.length > 0 || d.recherche?.zustand === "fehler") ? (
        <div className="lf-knoepfe pf-leiste">
          <button type="button" className="lf-knopf" disabled={recherche.isPending} onClick={() => recherche.mutate()}>
            {d.quellen.length ? "≈-Felder von Claude nachrecherchieren" : "Von Claude recherchieren lassen"}
          </button>
          {d.recherche?.zustand === "fehler" && <span className="lf-fehler">Letzte Recherche gescheitert{d.recherche.fehler ? `: ${d.recherche.fehler.slice(0, 120)}` : ""}</span>}
        </div>
      ) : null}
      {recherche.isError && <p className="lf-fehler">{fehlerText(recherche.error)}</p>}

      {bearbeiten ? <PflanzeFormular d={d} /> : (
        <section className="lf-abschnitt">
          <dl className="pf-daten">
            {zeilen.filter(([, w]) => w).map(([k, w, feld]) => (
              <div key={k}><dt>{k}</dt><dd>{w}{geschaetzt(feld)}</dd></div>
            ))}
          </dl>
          {d.hinweis && <p className="lf-stamm">{d.hinweis}</p>}
          {!!d.synonyme.length && <p className="lf-hinweis">Auch: {d.synonyme.join(" · ")}</p>}
          {!!d.handelsnamen.length && <p className="lf-hinweis">Auf Rechnungen: {d.handelsnamen.join(" · ")}</p>}
          {d.aus_wissen.length > 0 && <p className="lf-hinweis">≈ = aus Fachwissen, nicht aus einer Quelle — bitte gegenprüfen.</p>}
        </section>
      )}

      <section className="lf-abschnitt">
        <div className="lf-abschnitt-kopf"><h3>Lieferbar · Aufschlag ×{String(d.aufschlag).replace(".", ",")}</h3></div>
        {d.qualitaeten.length === 0 ? <p className="lf-leer">Noch von keinem Lieferanten bezogen — im Angebot ohne Preis.</p> : (
          <ul className="lf-liste">
            {d.qualitaeten.map((q) => q.lieferanten.length ? q.lieferanten.map((l) => (
              <li key={q.id + l.id} className="lf-zeile">
                <button type="button" className="lf-zeile-haupt" onClick={() => onLieferant(l.id)}>
                  <span className="lf-zeile-name">{q.bezeichnung || "Qualität unbekannt"}</span>
                  <span className="lf-zeile-info">{l.name}{l.preis_am ? ` · ${new Date(l.preis_am).toLocaleDateString("de-DE")}` : ""}</span>
                </button>
                <span className="pf-preis">
                  {l.preis_netto != null ? <>EK {euro(l.preis_netto)}<small>Angebot {euro(Math.round(l.preis_netto * d.aufschlag * 100) / 100)}</small></> : "—"}
                </span>
              </li>
            )) : (
              <li key={q.id} className="lf-zeile">
                <span className="lf-zeile-haupt"><span className="lf-zeile-name">{q.bezeichnung || "Qualität unbekannt"}</span>
                  <span className="lf-zeile-info">{q.material}</span></span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {!!d.quellen.length && (
        <section className="lf-abschnitt">
          <div className="lf-abschnitt-kopf"><h3>Quellen</h3></div>
          <ul className="lf-liste">
            {d.quellen.map((q, i) => (
              <li key={i} className="lf-zeile">
                <a className="lf-zeile-haupt" href={q.url} target="_blank" rel="noreferrer">
                  <span className="lf-zeile-name">{q.titel || q.url}</span>
                  <span className="lf-zeile-info">{(() => { try { return new URL(q.url).hostname; } catch { return q.url; } })()}</span>
                </a>
              </li>
            ))}
          </ul>
          {d.angelegt_von && <p className="lf-hinweis">Angelegt von {d.angelegt_von}.</p>}
        </section>
      )}
    </div>
  );
}

// ── Bearbeiten ───────────────────────────────────────────────────────────

function PflanzeFormular({ d }: { d: PflanzeBlatt }) {
  const aendern = usePflanzeAendern(d.id);
  const [f, setF] = useState(() => ({
    botanisch: d.botanisch, deutsch: d.deutsch, typ: d.typ,
    hoehe_von: d.hoehe_von ?? "", hoehe_bis: d.hoehe_bis ?? "", breite_von: d.breite_von ?? "", breite_bis: d.breite_bis ?? "",
    standort: d.standort, feuchte: d.feuchte, boden: d.boden,
    bluete_von: d.bluete_von ?? "", bluete_bis: d.bluete_bis ?? "", bluete_farbe: d.bluete_farbe,
    heimisch: d.heimisch, insektenfreundlich: d.insektenfreundlich, immergruen: d.immergruen, giftig: d.giftig,
    winterhaerte: d.winterhaerte, stueck_je_m2: d.stueck_je_m2 ?? "", hinweis: d.hinweis, synonyme: d.synonyme.join("; "),
  }));
  const zahl = (v: number | string) => (v === "" || v == null ? null : Number(String(v).replace(",", ".")));
  const speichern = () => {
    const w: PflanzeAenderung = {
      botanisch: f.botanisch, deutsch: f.deutsch, typ: f.typ,
      hoehe_von: zahl(f.hoehe_von), hoehe_bis: zahl(f.hoehe_bis), breite_von: zahl(f.breite_von), breite_bis: zahl(f.breite_bis),
      standort: f.standort, feuchte: f.feuchte, boden: f.boden,
      bluete_von: zahl(f.bluete_von), bluete_bis: zahl(f.bluete_bis), bluete_farbe: f.bluete_farbe,
      heimisch: f.heimisch, insektenfreundlich: f.insektenfreundlich, immergruen: f.immergruen, giftig: f.giftig,
      winterhaerte: f.winterhaerte, stueck_je_m2: zahl(f.stueck_je_m2), hinweis: f.hinweis,
      synonyme: f.synonyme.split(";").map((s) => s.trim()).filter(Boolean),
    };
    // Nur Geändertes schicken — sonst gälte jedes Feld als von Hand bestätigt (≈ verschwände).
    const alt: Record<string, unknown> = { ...d, synonyme: d.synonyme };
    const diff = Object.fromEntries(Object.entries(w).filter(([k, v]) => JSON.stringify(v ?? null) !== JSON.stringify(alt[k] ?? null)));
    if (Object.keys(diff).length) aendern.mutate(diff as PflanzeAenderung);
  };
  const umschalten = (feld: "standort" | "feuchte", wert: string) =>
    setF({ ...f, [feld]: f[feld].includes(wert) ? f[feld].filter((x) => x !== wert) : [...f[feld], wert] });
  const drei = (feld: "heimisch" | "insektenfreundlich" | "immergruen" | "giftig", label: string) => (
    <label className="lf-label">{label}
      <select className="lf-feld" value={f[feld] == null ? "" : f[feld] ? "ja" : "nein"}
        onChange={(e) => setF({ ...f, [feld]: e.target.value === "" ? null : e.target.value === "ja" })}>
        <option value="">unbekannt</option><option value="ja">ja</option><option value="nein">nein</option>
      </select>
    </label>
  );
  const monat = (feld: "bluete_von" | "bluete_bis", label: string) => (
    <label className="lf-label">{label}
      <select className="lf-feld" value={String(f[feld])} onChange={(e) => setF({ ...f, [feld]: e.target.value === "" ? "" : Number(e.target.value) })}>
        <option value="">—</option>
        {MONAT.slice(1).map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
    </label>
  );
  const feld = (k: keyof typeof f, label: string, art: "text" | "zahl" = "text") => (
    <label className="lf-label">{label}
      <input className="lf-feld" value={String(f[k] ?? "")} inputMode={art === "zahl" ? "decimal" : undefined}
        onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </label>
  );

  return (
    <section className="lf-abschnitt lf-form pf-form">
      {feld("botanisch", "Botanischer Name")}
      <div className="lf-form-zwei">
        {feld("deutsch", "Deutscher Name")}
        <label className="lf-label">Typ
          <select className="lf-feld" value={f.typ} onChange={(e) => setF({ ...f, typ: e.target.value as PflanzeBlatt["typ"] })}>
            {REIHE.map((t) => <option key={t} value={t}>{TYPNAME[t]}</option>)}
          </select>
        </label>
      </div>
      <div className="lf-form-zwei">{feld("hoehe_von", "Höhe von (cm)", "zahl")}{feld("hoehe_bis", "Höhe bis (cm)", "zahl")}</div>
      <div className="lf-form-zwei">{feld("breite_von", "Breite von (cm)", "zahl")}{feld("breite_bis", "Breite bis (cm)", "zahl")}</div>
      <div className="lf-label">Standort
        <div className="pf-wahl">{["sonne", "halbschatten", "schatten"].map((s) => (
          <button key={s} type="button" className={"lf-knopf" + (f.standort.includes(s) ? " ist-an" : "")} aria-pressed={f.standort.includes(s)} onClick={() => umschalten("standort", s)}>{s}</button>
        ))}</div>
      </div>
      <div className="lf-label">Feuchte
        <div className="pf-wahl">{["trocken", "frisch", "feucht", "nass", "wasser"].map((s) => (
          <button key={s} type="button" className={"lf-knopf" + (f.feuchte.includes(s) ? " ist-an" : "")} aria-pressed={f.feuchte.includes(s)} onClick={() => umschalten("feuchte", s)}>{s}</button>
        ))}</div>
      </div>
      {feld("boden", "Boden")}
      <div className="lf-form-zwei">{monat("bluete_von", "Blüte von")}{monat("bluete_bis", "Blüte bis")}</div>
      {feld("bluete_farbe", "Blütenfarbe")}
      <div className="lf-form-zwei">{drei("heimisch", "Heimisch")}{drei("insektenfreundlich", "Insektenfreundlich")}</div>
      <div className="lf-form-zwei">{drei("immergruen", "Immergrün")}{drei("giftig", "Giftig")}</div>
      <div className="lf-form-zwei">{feld("winterhaerte", "Winterhärte (z. B. Z5)")}{feld("stueck_je_m2", "Stück je m²", "zahl")}</div>
      <label className="lf-label">Hinweis
        <textarea className="lf-feld" rows={2} value={f.hinweis} onChange={(e) => setF({ ...f, hinweis: e.target.value })} />
      </label>
      {feld("synonyme", "Andere Namen (mit ; trennen)")}
      <div className="lf-knoepfe">
        {aendern.isSuccess && !aendern.isPending && <span className="lf-hinweis">Gespeichert.</span>}
        <button type="button" className="lf-knopf is-primaer" disabled={aendern.isPending} onClick={speichern}>
          {aendern.isPending ? "Speichert …" : "Speichern"}
        </button>
      </div>
      {aendern.isError && <p className="lf-fehler">{fehlerText(aendern.error)}</p>}
    </section>
  );
}
